import { sameCrmOwner } from "../crmRowAccess";
import type { CrmHandlerView } from "./crmHandlerFace";

/**
 * MAY THE VIEWER SET THE מתווך מטפל — one rule, one place.
 *
 * ── Why this is NOT a fifth `crmPermissions` axis ───────────────────────────────
 *
 * `crmPermissions` has pool / browse / export / delete / message and no `edit`.
 * `crmRowAccess.ts` makes the argument for the identical situation: inventing an axis is a
 * policy decision for agency owners to make, in a settings screen, with a migration for 96
 * existing orgs. Two facts the product already resolves server-side answer this instead —
 * "am I an admin of this agency" and "am I the one currently handling this person".
 *
 * The hand-off half is what keeps the feature usable without a manager in the loop: a
 * realtor going on holiday passes their leads to a colleague without opening a ticket. It is
 * strictly narrower than the admin power — it applies only to a contact the caller is
 * already designated for.
 *
 * ── The 27 admin-less agencies, and why they see an EXPLANATION rather than a button ──
 *
 * Measured across 96 orgs, 27 list nobody in `admins[]`. In those agencies nobody can make a
 * FIRST designation: there is no admin, and with nothing assigned there is no handler to
 * hand off from. That is a known dead end, deferred deliberately — the fix is a data
 * backfill, not a widened rule. So where there is room for a label the unassigned state says
 * `רק מנהל משרד יכול לשבץ` instead of rendering a control that cannot work; in a dense row
 * it renders nothing at all, because fifty copies of that sentence is not an explanation.
 *
 * ── `undefined` IS NOT "unassigned" ─────────────────────────────────────────────
 *
 * The lookup is a separate request that lands a beat after the list. Treating "not answered
 * yet" as "nobody is designated" would flash the hand-off affordance for a frame on every
 * load, which is the same class of bug `sameCrmOwner` refuses by never matching an absent id
 * against another absent id.
 */
export type CrmHandlerAccess = {
  /** The caller belongs to at least one agency. Everything here is false without it. */
  readonly inAgency: boolean;
  /** The caller administers the agency in question. */
  readonly isAdmin: boolean;
};

export const canAssignHandler = (
  access: CrmHandlerAccess,
  handler: CrmHandlerView | null | undefined,
  viewerUserId: string | null | undefined
): boolean => {
  if (!access.inAgency) return false;
  if (access.isAdmin) return true;
  // `undefined` (not answered) falls through to false — see the header.
  return handler ? sameCrmOwner(handler.userId, viewerUserId) : false;
};

/**
 * What the unassigned state SAYS to someone who may not set it.
 *
 * Returned rather than rendered so the dense surfaces (a 60px table cell, a 360px card line)
 * can choose to render nothing while the labelled ones (the panel, the detail page) print
 * it. Same withhold-don't-disable discipline, applied to a sentence instead of a control.
 */
export const CRM_HANDLER_ADMIN_ONLY_HINT = "רק מנהל משרד יכול לשבץ מתווך מטפל";
